import { api } from "./client";
import { unwrapApiData } from "./unwrap";

export interface QuoteLine {
  code: string;
  labelKey: string;
  amountMinor: number;
}

export interface BillingQuote {
  orgId: string;
  currency: string;
  lines: QuoteLine[];
  totalMinor: number;
  periodLabel: string;
}

export interface PaywallPayload {
  orgId: string;
  email: string;
  companyName: string;
  quote: BillingQuote;
  supportWhatsapp: string;
  paymentProvider: string;
  subscription?: {
    trialEnds: string | null;
    paidUntil: string | null;
    isTrial: boolean;
  };
}

export interface QuoteResponse {
  quote: BillingQuote;
  supportWhatsapp: string;
  paymentProvider: string;
  mobipayReady: boolean;
}

export async function fetchPublicQuote(
  orgId: string,
  email: string,
): Promise<QuoteResponse> {
  const res = await api.post<
    { ok: true; data: QuoteResponse } | import("./types").ApiErrorBody
  >("/v1/billing/quote/public", { orgId, email }, { skipAuth: true });
  return unwrapApiData(res);
}

export async function fetchAuthenticatedQuote(): Promise<QuoteResponse> {
  const res = await api.get<
    { ok: true; data: QuoteResponse } | import("./types").ApiErrorBody
  >("/v1/billing/quote");
  return unwrapApiData(res);
}

export async function startPaymobCheckout(payload: {
  orgId: string;
  email: string;
  password: string;
  billingPhone?: string;
  billingFirstName?: string;
  billingLastName?: string;
}): Promise<{
  checkoutId: string;
  iframeUrl: string;
  paymobOrderId: string;
  amountMinor: number;
  currency: string;
}> {
  const res = await api.post<
    | { ok: true; data: {
        checkoutId: string;
        iframeUrl: string;
        paymobOrderId: string;
        amountMinor: number;
        currency: string;
      } }
    | import("./types").ApiErrorBody
  >("/v1/billing/checkout/paymob", payload, { skipAuth: true });
  return unwrapApiData(res);
}

export async function getCheckoutStatus(checkoutId: string): Promise<{
  checkoutId: string;
  orgId: string;
  status: number;
  amountMinor: number;
  currency: string;
  paymobOrderId: string | null;
  updatedAt: string;
}> {
  const res = await api.get<
    | { ok: true; data: {
        checkoutId: string;
        orgId: string;
        status: number;
        amountMinor: number;
        currency: string;
        paymobOrderId: string | null;
        updatedAt: string;
      } }
    | import("./types").ApiErrorBody
  >(`/v1/billing/checkout/${checkoutId}/status`, { skipAuth: true });
  return unwrapApiData(res);
}

export async function startPublicCheckout(payload: {
  orgId: string;
  email: string;
  password: string;
}): Promise<{
  checkoutId: string;
  amountMinor: number;
  currency: string;
  message: string;
  mobipayCheckoutUrl: string | null;
}> {
  const res = await api.post<
    | { ok: true; data: {
        checkoutId: string;
        amountMinor: number;
        currency: string;
        message: string;
        mobipayCheckoutUrl: string | null;
      } }
    | import("./types").ApiErrorBody
  >("/v1/billing/checkout/public", payload, { skipAuth: true });
  return unwrapApiData(res);
}

export async function startCheckout(payload?: {
  billingPhone?: string;
  billingFirstName?: string;
  billingLastName?: string;
}): Promise<{
  checkoutId: string;
  iframeUrl: string;
  paymobOrderId: string;
  amountMinor: number;
  currency: string;
}> {
  const res = await api.post<
    | { ok: true; data: {
        checkoutId: string;
        iframeUrl: string;
        paymobOrderId: string;
        amountMinor: number;
        currency: string;
      } }
    | import("./types").ApiErrorBody
  >("/v1/billing/checkout/paymob/session", payload ?? {});
  return unwrapApiData(res);
}

const PAYWALL_STORAGE_KEY = "godo_paywall";
const PAYWALL_PWD_KEY = "godo_paywall_pwd";

export function storePaywallPayload(payload: PaywallPayload, checkoutPassword?: string): void {
  sessionStorage.setItem(PAYWALL_STORAGE_KEY, JSON.stringify(payload));
  if (checkoutPassword) {
    sessionStorage.setItem(PAYWALL_PWD_KEY, checkoutPassword);
  }
}

export function loadPaywallCheckoutPassword(): string | null {
  return sessionStorage.getItem(PAYWALL_PWD_KEY);
}

export function clearPaywallCheckoutPassword(): void {
  sessionStorage.removeItem(PAYWALL_PWD_KEY);
}

export function loadPaywallPayload(): PaywallPayload | null {
  const raw = sessionStorage.getItem(PAYWALL_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PaywallPayload;
  } catch {
    return null;
  }
}

export function clearPaywallPayload(): void {
  sessionStorage.removeItem(PAYWALL_STORAGE_KEY);
  clearPaywallCheckoutPassword();
}
